import { Table, Space, Title, Text, Checkbox } from '@bancobarigui/barihaus';
import { useMemo } from 'react';
import { DadosComparativoDireto } from '@/features/laudo/types/laudo-wizard.types';
import type { TabelaComparativoDiretoProps } from './types';
import {
  useDadosCalculados,
  useSincronizarDados,
  useMediaValorHomogeneizado,
  useMediaValorM2,
} from './hooks';
import { createColumns } from './components/TabelaComparativoDiretoColumns';
import { formatarMoeda } from '../shared/formatters';
import * as styles from './styles';
import { ReviewFieldWrapper } from '../../../ReviewFieldWrapper';

export function TabelaComparativoDireto({
  dados,
  onUpdate,
  parametrosFatoracao,
  onParametrosFatoracaoChange,
  disabled,
  displayIdMap,
}: TabelaComparativoDiretoProps) {
  const dadosCalculados = useDadosCalculados(dados, parametrosFatoracao);

  useSincronizarDados(dadosCalculados, dados, onUpdate);

  const mediaValorHomogeneizado = useMediaValorHomogeneizado(dadosCalculados);
  const mediaValorM2 = useMediaValorM2(dadosCalculados);

  // Usa identificador da amostra ao invés de índice para evitar problemas ao filtrar
  const handleInputChange = (amostraId: string, field: keyof DadosComparativoDireto, value: number) => {
    const novosDados = dados.map(item =>
      item.amostra === amostraId
        ? { ...item, [field]: value } as DadosComparativoDireto
        : item
    );
    onUpdate(novosDados);
  };

  // Filtra o "Avaliando" para exibição na tabela
  const dadosSemAvaliando = dadosCalculados.filter(d => d.amostra !== 'Avaliando');

  // Calcula limites ±30% para destacar outliers
  const limites = useMemo(() => {
    const valores = dadosSemAvaliando
      .map(d => d.valorHomogeneizado)
      .filter(v => v > 0);
    if (valores.length === 0) return { limiteInferior: 0, limiteSuperior: 0 };
    const media = valores.reduce((acc, val) => acc + val, 0) / valores.length;
    return { limiteInferior: media * 0.7, limiteSuperior: media * 1.3 };
  }, [dadosSemAvaliando]);

  const columns = createColumns(parametrosFatoracao, handleInputChange, disabled, displayIdMap);
  
  return (
    <div style={styles.containerStyle}>
      <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
        <ReviewFieldWrapper
          fieldPath="amostras.tabelaComparativoDireto"
          fieldLabel="Tabela de Homogeneização (Comparativo Direto)"
          stepName="Metodologia"
          linkInline={true}
        >
          <Title level={4} style={{ margin: 0 }}>
            Tabela de homogeneização
          </Title>
        </ReviewFieldWrapper>
      </Space>

      <div style={{ marginBottom: 16 }}>
        <Checkbox.Group
          value={parametrosFatoracao}
          onChange={onParametrosFatoracaoChange}
          disabled={disabled}
          options={[
            { label: 'Região', value: 'regiao' },
            { label: 'Andar', value: 'andar' },
            { label: 'Vaga', value: 'vaga' },
            { label: 'Área', value: 'area' },
            { label: 'Padrão Acabamento', value: 'padraoAcabamento' },
            { label: 'Estado Conservação', value: 'estadoConservacao' },
            { label: 'Fator Extra I', value: 'fatorExtraI' },
            { label: 'Fator Extra II', value: 'fatorExtraII' },
            { label: 'Fator Extra III', value: 'fatorExtraIII' },
          ]}
        />
      </div>
      
      <Table
        dataSource={dadosSemAvaliando}
        columns={columns}
        rowKey="amostra"
        pagination={false}
        scroll={{ x: 1200 }}
        bordered
        size="small"
        rowClassName={(record) => {
          if (record.valorHomogeneizado > 0 && limites.limiteSuperior > 0) {
            const fora = record.valorHomogeneizado < limites.limiteInferior ||
              record.valorHomogeneizado > limites.limiteSuperior;
            if (fora) return 'row-fora-intervalo';
          }
          return '';
        }}
      />

      <div style={styles.mediaContainerStyle}>
        <Space direction="vertical" size={4}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Média dos Valores Homogeneizados
          </Text>
          <Text strong style={{ fontSize: 20, color: '#1890ff' }}>
            {formatarMoeda(mediaValorHomogeneizado)}
          </Text>
        </Space>
      </div>

      <style>{styles.tableStyles}</style>
    </div>
  );
}
